import React, { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Layers, AlertTriangle } from 'lucide-react';

/**
 * User Workspace Assignment Dialog
 * Allows admins to assign workspaces to users and set their default workspace.
 */
const UserWorkspaceAssignment = ({ 
  user, 
  onUpdate,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState([]);
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (open) {
      loadWorkspaces();
      // Initialize from user data
      setSelectedWorkspaceIds(user.workspace_ids || []);
      setDefaultWorkspaceId(user.default_workspace_id || "");
      setValidationError("");
    }
  }, [open, user]);

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tenants/workspaces/all');
      setWorkspaces(response.data);
    } catch (err) {
      toast({
        title: "Failed to load workspaces",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceToggle = (workspaceId, checked) => {
    let newSelection;
    if (checked) {
      newSelection = [...selectedWorkspaceIds, workspaceId];
      // Auto-set default if this is the first selection
      if (newSelection.length === 1) {
        setDefaultWorkspaceId(workspaceId);
      }
    } else {
      // Cannot remove if it's the default
      if (workspaceId === defaultWorkspaceId) {
        setValidationError("Cannot remove the default workspace. Change the default first.");
        return;
      }
      newSelection = selectedWorkspaceIds.filter(id => id !== workspaceId);
    }
    setSelectedWorkspaceIds(newSelection);
    setValidationError("");
    
    // If default is not in selection, update it
    if (defaultWorkspaceId && !newSelection.includes(defaultWorkspaceId)) {
      setDefaultWorkspaceId(newSelection[0] || "");
    }
  };

  const handleDefaultChange = (workspaceId) => {
    // Default must be in selected workspaces
    if (workspaceId && !selectedWorkspaceIds.includes(workspaceId)) {
      setValidationError("Default workspace must be selected first");
      return;
    }
    setDefaultWorkspaceId(workspaceId);
    setValidationError("");
  };

  const handleSave = async () => {
    // Validation
    if (selectedWorkspaceIds.length > 0 && !defaultWorkspaceId) {
      setValidationError("Please select a default workspace");
      return;
    }
    
    if (defaultWorkspaceId && !selectedWorkspaceIds.includes(defaultWorkspaceId)) {
      setValidationError("Default workspace must be in the selected workspaces");
      return;
    }
    
    try {
      setSubmitting(true);
      await api.put(`/tenants/users/${user.id}/workspaces`, {
        workspace_ids: selectedWorkspaceIds,
        default_workspace_id: defaultWorkspaceId || null,
      });
      
      toast({ title: "Workspace assignments updated" });
      setOpen(false);
      onUpdate?.();
      
    } catch (err) {
      toast({
        title: "Failed to update workspaces",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getWorkspaceName = (id) => {
    const ws = workspaces.find(w => w.id === id);
    return ws?.name || id?.substring(0, 8) + "...";
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 border-slate-600 text-slate-200 hover:bg-slate-800"
            data-testid={`user-workspace-btn-${user.id}`}
          >
            <Layers className="h-3 w-3 mr-1" />
            {(user.workspace_ids?.length || 0)} Workspace{(user.workspace_ids?.length || 0) !== 1 ? 's' : ''}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-100">Workspace Access</DialogTitle>
          <DialogDescription className="text-slate-400">
            Assign workspaces to {user.email || user.first_name || "this user"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Workspace Selection */}
          <div className="space-y-3">
            <Label className="text-slate-200">Assigned Workspaces</Label>
            {loading ? (
              <div className="text-sm text-slate-400">Loading...</div>
            ) : workspaces.length === 0 ? (
              <div className="text-sm text-slate-400">No workspaces available</div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {workspaces
                  .filter(ws => ws.status === "active" || !ws.status)
                  .map((ws) => (
                    <div
                      key={ws.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/10"
                    >
                      <Checkbox
                        id={`ws-${ws.id}`}
                        checked={selectedWorkspaceIds.includes(ws.id)}
                        onCheckedChange={(checked) => handleWorkspaceToggle(ws.id, checked)}
                        data-testid={`workspace-checkbox-${ws.id}`}
                      />
                      <label
                        htmlFor={`ws-${ws.id}`}
                        className="flex-1 text-sm text-slate-200 cursor-pointer"
                      >
                        {ws.name}
                      </label>
                      {ws.id === defaultWorkspaceId && (
                        <Badge variant="outline" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
          
          {/* Default Workspace */}
          {selectedWorkspaceIds.length > 0 && (
            <div className="space-y-2">
              <Label className="text-slate-200">Default Workspace</Label>
              <Select
                value={defaultWorkspaceId || selectedWorkspaceIds[0] || ""}
                onValueChange={(v) => handleDefaultChange(v)}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100">
                  <SelectValue placeholder="Select default..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {selectedWorkspaceIds.map((wsId) => {
                    const wsName = getWorkspaceName(wsId);
                    return (
                      <SelectItem key={wsId} value={wsId} className="text-slate-100">
                        {wsName}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                The default workspace is used when creating new leads
              </p>
            </div>
          )}
          
          {/* Validation Error */}
          {validationError && (
            <div className="flex items-center gap-2 text-sm text-amber-400 p-2 bg-amber-500/10 rounded-lg">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {validationError}
            </div>
          )}
          
          {/* Info Box */}
          <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
            <p className="text-xs text-slate-300">
              <strong>RBAC Note:</strong> Users can only see data in their assigned workspaces. 
              Removing a workspace will hide that workspace's data from this user.
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setOpen(false)} 
            className="border-slate-600 text-slate-200 hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={submitting}
            className="bg-indigo-500/90 text-white hover:bg-indigo-500"
            data-testid="save-workspace-assignment"
          >
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserWorkspaceAssignment;
